import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLogout } from 'new/modules/LoginPage/hooks';
import Modal from 'new/components/Modal';
import BillingIssueInfo from 'new/components/Disputes/components/BillingIssueInfo';
import StripeRestrictedInfo from 'new/components/Disputes/components/StripeRestrictedInfo';
import Button from 'new/components/Button/Button';
import { handleLogout } from 'new/components/AppBar/SidebarPopoverContent';
import { useDisputesData } from './Disputes/hooks';
import { TENANT } from 'new/tenants';

type RestricitonData = {
  name: string;
  restrictionDate: string | null;
};

type Props = {
  isPausedAccount?: boolean;
  restrictionDate?: string | null;
  multiOfficeRestrictionData?: RestricitonData[];
};

const dayInMilliseconds = 24 * 60 * 60 * 1000;
const delay2weeks = 2 * 7 * dayInMilliseconds;

const StyledDivModalWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  display: open ? 'block' : 'none',
  position: 'fixed',
  zIndex: 1300,
  top: 0,
  left: 0,
  background: theme.palette.grey[400],
  width: '100%',
  height: '100vh',
  opacity: 0.8,
}));

const StyledDivModalContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: 500,
  height: 300,
});

const StyledDivRestrictionWarning = styled('div')(({ theme }) => ({
  color: theme.palette.error.main,
  overflowY: 'auto',
  maxHeight: '100px',
  span: {
    fontWeight: 'bold',
  },
}));

const ModalForceLogOut: React.FC<Props> = ({
  isPausedAccount,
  restrictionDate,
  multiOfficeRestrictionData,
}) => {
  const [open, setOpen] = useState(true);
  const { stripeRestricted, billingIssue, remediationLink } = useDisputesData();

  const { logout } = useLogout();

  const Wrapper = ({ children }: { children: React.ReactNode }) => (
    <StyledDivModalWrapper open={open}>
      <Modal
        open={open}
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <StyledDivModalContent>{children}</StyledDivModalContent>
      </Modal>
    </StyledDivModalWrapper>
  );

  if (isPausedAccount) {
    return (
      <Wrapper>
        <Typography>
          Your account is currently
          <strong> paused</strong>. You cannot add members and will not be
          charged while paused. Please reach out to{' '}
          <strong>success@dentalhq.com</strong> for assistance.
        </Typography>
        <Button onClick={() => handleLogout(logout)}>LOG OUT</Button>
      </Wrapper>
    );
  }

  const getMiliseconds = (date?: string | null) =>
    new Date(date || '').getTime();

  const todayInMiliseconds = Date.now();
  const deadlineInMiliseconds = getMiliseconds(restrictionDate) + delay2weeks;

  const isAfterDeadline =
    remediationLink == 'billing' || billingIssue
      ? false
      : todayInMiliseconds > deadlineInMiliseconds;

  const countDaysLeft = (officeRestrictionDate: string) => {
    const milisecondsLeft =
      getMiliseconds(officeRestrictionDate) + delay2weeks - todayInMiliseconds;

    if (milisecondsLeft < 0) {
      return -1;
    }

    return Math.floor(milisecondsLeft / dayInMilliseconds);
  };

  const onClose = () => {
    setOpen(false);
    sessionStorage.setItem('wasDisplayedStripeRestrictionModal', 'true');
  };

  if (
    (restrictionDate || multiOfficeRestrictionData?.length) &&
    sessionStorage.getItem('wasDisplayedStripeRestrictionModal') !== 'true'
  ) {
    const multiOfficeWarning = multiOfficeRestrictionData?.map((office) =>
      office.restrictionDate && countDaysLeft(office.restrictionDate) > -1 ? (
        <Typography>
          <span>{office.name}</span> will be restricted in{' '}
          <span>{countDaysLeft(office.restrictionDate)} days</span>
        </Typography>
      ) : (
        <Typography>
          <span>{office.name} is restricted</span>
        </Typography>
      ),
    );

    const singleOfficeWarning = (
      <Typography>
        {isAfterDeadline ? (
          <span>Restriction Warning</span>
        ) : remediationLink == 'billing' || billingIssue ? (
          <span>Payout Failure Warning</span>
        ) : (
          <span>
            Your office will be restricted from access to the platform{' '}
            {restrictionDate && `in ${countDaysLeft(restrictionDate)} days`}
          </span>
        )}
      </Typography>
    );

    return (
      <Wrapper>
        <StyledDivRestrictionWarning>
          {multiOfficeRestrictionData
            ? multiOfficeWarning
            : singleOfficeWarning}
        </StyledDivRestrictionWarning>
        {billingIssue && (
          <BillingIssueInfo
            isAfterDeadline={isAfterDeadline && !multiOfficeRestrictionData}
            onClick={onClose}
          />
        )}
        {stripeRestricted && (
          <StripeRestrictedInfo remediationLink={remediationLink} />
        )}
        {multiOfficeRestrictionData && (
          <Typography>
            <span>
              Please view the location to correct or contact{' '}
              <a href={`mailto:support@${TENANT.prodDomain}`} target="_blank">
                support@{TENANT.prodDomain}
              </a>
            </span>
          </Typography>
        )}
        {isAfterDeadline && !multiOfficeRestrictionData?.length ? (
          <Button onClick={() => handleLogout(logout)}>LOG OUT</Button>
        ) : (
          <Button onClick={onClose}>CLOSE</Button>
        )}
      </Wrapper>
    );
  }

  return null;
};

export default ModalForceLogOut;
